// @flow
import type { Props } from './types';

import React from 'react';
import { FormattedMessage } from 'react-intl';
import { getOr } from 'lodash/fp';
import { actionTypes } from '~/core/constants/actions/';
import generalIcons from '~/core/constants/icons/general-svg';
import SVGIcon from '~/core/icons/svg-icons';
import { ToggleButton } from '~/components/widgets';
import ActionAttribute from '../../action-attribute';
import IconsSummary from '../../icons-summary/component';
import AddEvent from './add-event/component';
import messages from './intl';
import {
    ActionContainer,
    ActionNameInput,
    EditIconContainer,
    EventTitle,
    Line,
    Remove,
    Section,
    ToggleContainer
} from './styles';

const MAX_NAME_LENGTH = 128;
const MAX_NAME_LENGTH_QUICK_ACTON = 40;
const QUICK_ACTION = 'quick-action';

const getIcon = (key, obj) => getOr(null, [key, '0', 'definition', 'icon'], obj);

const ActionDetail = ({
    actionNameEdited,
    addActionAttribute,
    allowedTypes,
    editActionName,
    extendedForm,
    isNano1,
    removeActionAttribute,
    region,
    setActionAttribute,
    setActionForm,
    showConfirmModal
}: Props) => {
    const whenLength = getOr(0, ['when', 'length'], extendedForm);
    const whileLength = getOr(0, ['while', 'length'], extendedForm);
    const thenLength = getOr(0, ['then', 'length'], extendedForm);
    const isQuickAction = getOr('', ['when', '0', 'type'], extendedForm) === QUICK_ACTION;

    return (
        <ActionContainer>
            <IconsSummary
                {...(whileLength > 0 ? { _while: getIcon('while', extendedForm) } : {})}
                {...(thenLength > 0 ? { then: { type: getIcon('then', extendedForm) } } : {})}
                when={whenLength > 0 ? getIcon('when', extendedForm) : generalIcons.QUESTION}
            />
            <Line />
            {actionNameEdited === null ? (
                <Section>
                    <div>{extendedForm.name}</div>
                    <EditIconContainer onClick={() => editActionName(extendedForm.name)}>
                        <SVGIcon type={generalIcons.EDIT} />
                    </EditIconContainer>
                </Section>
            ) : (
                <FormattedMessage {...messages.nameYourAction}>
                    {message => (
                        <ActionNameInput
                            maxLength={isQuickAction ? MAX_NAME_LENGTH_QUICK_ACTON : MAX_NAME_LENGTH}
                            onChange={(e: SyntheticEvent<HTMLInputElement>) => editActionName(e.currentTarget.value)}
                            placeholder={message}
                            type="text"
                            value={actionNameEdited}
                        />
                    )}
                </FormattedMessage>
            )}

            <Line />
            {!extendedForm.when.find(event => event.type === actionTypes.QUICK_ACTION) && (
                <Section>
                    <div>
                        <FormattedMessage {...messages.enableAction} />
                    </div>
                    <ToggleContainer>
                        <ToggleButton
                            isOn={extendedForm.enabled}
                            onChange={() => setActionForm('enabled', !extendedForm.enabled)}
                        />
                    </ToggleContainer>
                </Section>
            )}
            {['when', 'while', 'then'].map(
                group =>
                    (group === 'when' || allowedTypes[group].length > 0) && (
                        <div key={group}>
                            <EventTitle>
                                <FormattedMessage {...messages[group]} />
                            </EventTitle>
                            {extendedForm[group].map((event, index) => (
                                <ActionAttribute
                                    actionId={extendedForm.id}
                                    event={event}
                                    formStatus={extendedForm.status}
                                    key={event.guid}
                                    removeActionAttribute={removeActionAttribute}
                                    setActionAttribute={(attributeProperty, newValue) =>
                                        setActionAttribute({
                                            attributeIndex: index,
                                            attributeProperty,
                                            group,
                                            newValue
                                        })}
                                />
                            ))}
                            {group !== 'when' &&
                                (group !== 'while' || getOr(0, [group, 'length'], extendedForm) === 0) &&
                                (group !== 'then' || (!isNano1 || getOr(0, [group, 'length'], extendedForm) === 0)) && (
                                    <AddEvent
                                        addActionAttribute={addActionAttribute}
                                        allowedTypes={allowedTypes[group]}
                                    />
                                )}
                        </div>
                    )
            )}
            {/* // Will remove the span of the following code by Fragment once we have managed to upgrade React */}
            {extendedForm.id && (
                <span>
                    <Line />
                    <Section>
                        <Remove onClick={() => extendedForm.id && showConfirmModal(extendedForm.id, extendedForm.name)}>
                            <FormattedMessage {...messages.removeAction} />
                        </Remove>
                    </Section>
                </span>
            )}
        </ActionContainer>
    );
};

export default ActionDetail;



// WEBPACK FOOTER //
// ./src/components/widgets/actions/entry/action-detail/component.js